/* Sticker Distortion — VyzStudio v5.7 QA — circular white-border dilation + medium cutline smoothing
 * Works from the local raster file selected by the existing immutable artwork uploader.
 * No server mutations, no Shopify native cart. Does not assert production cutline accuracy.
 * Non-default visual edits block cart submission until derivative persistence exists.
 */
(() => {
  'use strict';
  const W = 600, H = 500, MAX_SOURCE = 1200;
  const money = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
  const allowed = /\.(png|jpe?g|webp)$/i;

  // QA v5.4: edge-connected background extraction for any approximately uniform color.
  // Never removes interior islands not connected to the image edge.
  // Fail closed on multicolored/photo backgrounds or over-removal.
  // QA v5.5: conservative detached-speck cleanup; executed ONLY after user requests
  // background extraction, never on originals or multi-piece artwork.
  function cleanDetachedSpecks(pixels,w,h){
  const n=w*h, labels=new Int32Array(n), queue=new Uint32Array(n), parts=[];
  let total=0;
  for(let i=0;i<n;i++){
    if(labels[i]||pixels[i*4+3]<40)continue;
    if(parts.length>=32)return 0;
    const id=parts.length+1;
    let head=0,tail=0,count=0;
    let minX=w,minY=h,maxX=-1,maxY=-1;
    labels[i]=id;queue[tail++]=i;
    while(head<tail){
      const k=queue[head++],x=k%w,y=(k/w)|0;
      count++;if(x<minX)minX=x;if(x>maxX)maxX=x;
      if(y<minY)minY=y;if(y>maxY)maxY=y;
      for(let dy=-1;dy<=1;dy++)for(let dx=-1;dx<=1;dx++){
        if(!dx&&!dy)continue;
        const nx=x+dx,ny=y+dy;
        if(nx<0||nx>=w||ny<0||ny>=h)continue;
        const next=ny*w+nx;
        if(labels[next]||pixels[next*4+3]<40)continue;
        labels[next]=id;queue[tail++]=next;
      }
    }
    parts.push({id,count,minX,maxX,minY,maxY});
    total+=count;
  }
  if(parts.length<2)return 0;
  let main=parts[0];
  for(const p of parts)if(p.count>main.count)main=p;
  if(main.count<Math.max(500,n*.015)||main.count/total<.92)return 0;
  const maxArea=Math.min(n*.0025,main.count*.0075);
  const maxSide=Math.min(w,h)*.065;
  const minGap=Math.max(6,Math.min(w,h)*.025);
  const rejected=new Set();
  for(const p of parts){
    if(p===main)continue;
    const dx=Math.max(0,main.minX-p.maxX,p.minX-main.maxX);
    const dy=Math.max(0,main.minY-p.maxY,p.minY-main.maxY);
    const gap=Math.hypot(dx,dy);
    if(p.count<=maxArea && p.maxX-p.minX+1<=maxSide &&
       p.maxY-p.minY+1<=maxSide && gap>=minGap)rejected.add(p.id);
  }
  if(!rejected.size)return 0;
  for(let i=0;i<n;i++)if(rejected.has(labels[i]))pixels[i*4+3]=0;
  return rejected.size;
}

  function edgeBackgroundRemoved(source) {
    const w=source.width,h=source.height;
    const ctx=source.getContext('2d',{willReadFrequently:true});
    const im=ctx.getImageData(0,0,w,h), d=im.data;
    const sx=Math.max(1,Math.min(5,Math.floor(w*.012)));
    const sy=Math.max(1,Math.min(5,Math.floor(h*.012)));
    const corners=[[sx,sy],[w-1-sx,sy],[sx,h-1-sy],[w-1-sx,h-1-sy]];
    const colors=corners.map(([cx,cy])=>{
      const v=[[],[],[]];
      for(let dy=-2;dy<=2;dy++)for(let dx=-2;dx<=2;dx++){
        const x=Math.max(0,Math.min(w-1,cx+dx)),y=Math.max(0,Math.min(h-1,cy+dy));
        const p=(y*w+x)*4;
        if(d[p+3]<150)continue;
        v[0].push(d[p]);v[1].push(d[p+1]);v[2].push(d[p+2]);
      }
      return v[0].length?v.map(a=>a.sort((a,b)=>a-b)[a.length>>1]):null;
    });
    const distance=(a,b)=>Math.sqrt(.7*(a[0]-b[0])**2+(a[1]-b[1])**2+.65*(a[2]-b[2])**2);
    const candidates=colors.filter(Boolean);
    if(candidates.length<3)return {ok:false,reason:'Background removal could not identify a consistent exterior. Use a transparent PNG.'};
    let best=[];
    for(const candidate of candidates){
      const group=candidates.filter(color=>distance(candidate,color)<=52);
      if(group.length>best.length)best=group;
    }
    if(best.length<3)return {ok:false,reason:'The outside background has too many different colors. Use a transparent PNG or a solid-color background.'};
    const anchor=[0,1,2].map(i=>Math.round(best.reduce((v,c)=>v+c[i],0)/best.length));
    const diffs=[];
    for(const [cx,cy] of corners)for(let dy=-3;dy<=3;dy++)for(let dx=-3;dx<=3;dx++){
      const x=Math.max(0,Math.min(w-1,cx+dx)),y=Math.max(0,Math.min(h-1,cy+dy));
      const p=(y*w+x)*4;
      if(d[p+3]>=150)diffs.push(Math.sqrt(.7*(d[p]-anchor[0])**2+(d[p+1]-anchor[1])**2+.65*(d[p+2]-anchor[2])**2));
    }
    diffs.sort((a,b)=>a-b);
    const p90=diffs.length?diffs[Math.floor(.90*(diffs.length-1))]:0;
    const tolerance=Math.max(28,Math.min(90,Math.round(p90*2.4+18)));
    const tolSq=tolerance*tolerance;
    const seen=new Uint8Array(w*h),queue=new Uint32Array(w*h);
    let head=0,tail=0;
    function visit(i){
      if(seen[i])return;
      const p=i*4;
      const dr=d[p]-anchor[0],dg=d[p+1]-anchor[1],db=d[p+2]-anchor[2];
      if(d[p+3]<40 || .7*dr*dr+dg*dg+.65*db*db<=tolSq){
        seen[i]=1;queue[tail++]=i;
      }
    }
    for(let x=0;x<w;x++){visit(x);visit((h-1)*w+x);}
    for(let y=0;y<h;y++){visit(y*w);visit(y*w+w-1);}
    while(head<tail){
      const i=queue[head++],x=i%w,y=(i/w)|0;
      if(x>0)visit(i-1);if(x<w-1)visit(i+1);
      if(y>0)visit(i-w);if(y<h-1)visit(i+w);
    }
    if(tail<w*h*.012 || tail>w*h*.985)
      return {ok:false,reason:'Background extraction was not reliable. No pixels were removed. Try a transparent PNG.'};
    let remainingEdge=0;
    for(let x=0;x<w;x++){
      if(!seen[x]&&d[x*4+3]>=120)remainingEdge++;
      const j=(h-1)*w+x;
      if(!seen[j]&&d[j*4+3]>=120)remainingEdge++;
    }
    for(let y=0;y<h;y++){
      const i=y*w,j=i+w-1;
      if(!seen[i]&&d[i*4+3]>=120)remainingEdge++;
      if(!seen[j]&&d[j*4+3]>=120)remainingEdge++;
    }
    if(remainingEdge>2*(w+h)*.08)
      return {ok:false,reason:'The background is too textured or multicolored. No pixels were removed. Use a transparent PNG.'};
    for(let i=0;i<seen.length;i++)if(seen[i])d[i*4+3]=0;
    const cleaned=cleanDetachedSpecks(d,w,h);
    ctx.putImageData(im,0,0);
    return {ok:true,removed:tail,total:w*h,cleaned};
  }

  // QA v5.7: exact Euclidean-radius disk dilation, O(width*height*radius).
  // Unlike square separable dilation, circular offsets do not flatten the
  // corners of the external white border and its matching vector cutline.
  // Uses capped horizontal distance followed by per-row circular checks.
  function dilate(mask, w, h, radius) {
    if (!radius) return mask;
    const limit=radius+1, dist=new Uint16Array(mask.length), out=new Uint8Array(mask.length);
    for(let y=0;y<h;y++){
      const base=y*w;
      let last=-limit;
      for(let x=0;x<w;x++){
        if(mask[base+x])last=x;
        const d=x-last;
        dist[base+x]=d<=limit?d:limit;
      }
      last=w+limit;
      for(let x=w-1;x>=0;x--){
        if(mask[base+x])last=x;
        const d=last-x;
        if(d<dist[base+x])dist[base+x]=d;
      }
    }
    const allowedX=new Uint16Array(2*radius+1);
    for(let dy=-radius;dy<=radius;dy++){
      allowedX[dy+radius]=Math.floor(Math.sqrt(radius*radius-dy*dy));
    }
    for(let y=0;y<h;y++){
      const row=y*w;
      for(let x=0;x<w;x++){
        const i=row+x;
        if(mask[i]){out[i]=1;continue;}
        for(let dy=-radius;dy<=radius;dy++){
          const y2=y+dy;
          if(y2>=0 && y2<h && dist[y2*w+x]<=allowedX[dy+radius]){
            out[i]=1;
            break;
          }
        }
      }
    }
    return out;
  }

  // QA v5.6: medium smoothing; output is still M/Q/Z, so the existing
  // backend geometry contract and independent PDF vector layers remain valid.
  // RDP on closed quadratic control vertices with drift/topology safeguards.
  function simplifyClosedCutline(points,tolerance) {
    if(points.length<80 || !(tolerance>0))return points;
    function rdpOpen(items) {
      if(items.length<=2)return items;
      const a=items[0],b=items[items.length-1];
      const dx=b[0]-a[0],dy=b[1]-a[1],denom=dx*dx+dy*dy;
      let maxDist=-1,split=-1;
      for(let i=1;i<items.length-1;i++){
        const p=items[i],t=denom?Math.max(0,Math.min(1,((p[0]-a[0])*dx+(p[1]-a[1])*dy)/denom)):0;
        const dist=Math.hypot(p[0]-(a[0]+t*dx),p[1]-(a[1]+t*dy));
        if(dist>maxDist){maxDist=dist;split=i;}
      }
      if(maxDist<=tolerance)return [a,b];
      return rdpOpen(items.slice(0,split+1)).slice(0,-1).concat(rdpOpen(items.slice(split)));
    }
    let far=0,dist=-1;
    for(let i=1;i<points.length;i++){
      const value=Math.hypot(points[i][0]-points[0][0],points[i][1]-points[0][1]);
      if(value>dist){dist=value;far=i;}
    }
    if(far<=0 || far>=points.length-1)return points;
    const left=rdpOpen(points.slice(0,far+1)),right=rdpOpen(points.slice(far).concat([points[0]]));
    const reduced=left.slice(0,-1).concat(right.slice(0,-1));
    if(reduced.length<12 || reduced.length>=points.length)return points;
    function polygonArea(items){
      let value=0;
      for(let i=0;i<items.length;i++){
        const p=items[i],q=items[(i+1)%items.length];
        value+=p[0]*q[1]-q[0]*p[1];
      }
      return Math.abs(value*.5);
    }
    const area=polygonArea(points);
    if(area<250 || Math.abs(polygonArea(reduced)-area)/area>.01)return points;
    function crosses(a,b,c,d){
      const orient=(p,q,r)=>(q[0]-p[0])*(r[1]-p[1])-(q[1]-p[1])*(r[0]-p[0]);
      const x=orient(a,b,c),y=orient(a,b,d),z=orient(c,d,a),w=orient(c,d,b);
      return ((x>0&&y<0)||(x<0&&y>0))&&((z>0&&w<0)||(z<0&&w>0));
    }
    for(let i=0;i<reduced.length;i++){
      const i2=(i+1)%reduced.length;
      for(let j=i+2;j<reduced.length;j++){
        const j2=(j+1)%reduced.length;
        if(j2===i)continue;
        if(crosses(reduced[i],reduced[i2],reduced[j],reduced[j2]))return points;
      }
    }
    return reduced;
  }

  // Trace the external edges of a raster mask as clockwise, joined loops.
  // Small isolated pixels and holes are ignored: this is a PREVIEW ONLY, never a cut file.
  function vectorOutlines(mask, width, height, smoothMedium = false, tolerance = 0) {
    const outgoing = new Map();
    const key = (x,y) => y*(width+1)+x;
    const edges = [];
    const add = (x1,y1,x2,y2) => {
      const index=edges.length;
      edges.push([x1,y1,x2,y2]);
      const k=key(x1,y1);
      if (!outgoing.has(k)) outgoing.set(k, []);
      outgoing.get(k).push(index);
    };
    for (let y=0;y<height;y++) for(let x=0;x<width;x++) {
      const i=y*width+x;
      if (!mask[i]) continue;
      if (y===0 || !mask[i-width]) add(x,y,x+1,y);
      if (x===width-1 || !mask[i+1]) add(x+1,y,x+1,y+1);
      if (y===height-1 || !mask[i+width]) add(x+1,y+1,x,y+1);
      if (x===0 || !mask[i-1]) add(x,y+1,x,y);
    }
    const edgeUsed=new Uint8Array(edges.length), result=[];
    for (let start=0;start<edges.length;start++) {
      if(edgeUsed[start]) continue;
      let current=start, steps=0, closed=false;
      const pts=[], first=edges[start], begin=key(first[0], first[1]);
      while(steps++<edges.length) {
        if(edgeUsed[current]) break;
        edgeUsed[current]=1;
        const e=edges[current];pts.push([e[0],e[1]]);
        const dest=key(e[2],e[3]);
        if(dest===begin) {closed=true;break;}
        const found=(outgoing.get(dest)||[]).find(n=>!edgeUsed[n]);
        if(found===undefined) break;
        current=found;
      }
      if(!closed || pts.length<10) continue;
      let area=0;
      for(let i=0;i<pts.length;i++) {
        const p=pts[i], q=pts[(i+1)%pts.length];area+=p[0]*q[1]-q[0]*p[1];
      }
      // Positive winding = exterior contour; skip internal voids and noise.
      if(area<=120) continue;
      // Average neighboring subpixel steps to suppress pixel staircase; keep
      // original proportions. SVG smooth strokes handle final antialiasing.
      let points=pts;
      for(let round=0;round<2;round++) {
        points=points.map((p,i)=>{
          const previous=points[(i-1+points.length)%points.length];
          const next=points[(i+1)%points.length];
          return [(previous[0]+2*p[0]+next[0])/4,(previous[1]+2*p[1]+next[1])/4];
        });
      }
      points=points.filter((_,i)=>i%2===0);
      if(smoothMedium && area>1000) points=simplifyClosedCutline(points,tolerance);
      if(points.length<3) continue;
      const fmt=n=>Number(n.toFixed(2));
      const midpoint=(a,b)=>[(a[0]+b[0])/2,(a[1]+b[1])/2];
      const firstPoint=midpoint(points[points.length-1],points[0]);
      let path=`M${fmt(firstPoint[0])} ${fmt(firstPoint[1])}`;
      for(let i=0;i<points.length;i++) {
        const p=points[i], q=points[(i+1)%points.length], mid=midpoint(p,q);
        path+=`Q${fmt(p[0])} ${fmt(p[1])} ${fmt(mid[0])} ${fmt(mid[1])}`;
      }
      result.push({area,path:path+'Z'});
    }
    return result.sort((a,b)=>b.area-a.area).slice(0,16).map(p=>p.path).join(' ');
  }


  // SD8: same fit transformation must apply to displayed image, saved PNG,
  // vector white border and immutable signed SVG cutline.
  function fitContourToPaidSize(svg,frame){
    const t=(String(svg||'').match(/[MQZ]|-?(?:\d+(?:\.\d+)?|\.\d+)/g)||[]);
    if(!t.length)return null;
    let i=0,last=null,first=null,segments=0;
    const vertex=[],ctrl=[];
    const read=()=>{const x=Number(t[i++]),y=Number(t[i++]);if(!Number.isFinite(x)||!Number.isFinite(y))throw Error('invalid SVG coordinate');return [x,y];};
    const ext=(a,b,c)=>{const den=a-2*b+c;if(Math.abs(den)<1e-12)return null;const at=(a-b)/den;return at>0&&at<1?(1-at)**2*a+2*(1-at)*at*b+at**2*c:null;};
    try{
      while(i<t.length){
        const cmd=t[i++];
        if(cmd==='M'){last=first=read();vertex.push(last);ctrl.push(last);}
        else if(cmd==='Q'&&last){
          const cp=read(),end=read();
          ctrl.push(cp,end);vertex.push(end);
          const ex=ext(last[0],cp[0],end[0]),ey=ext(last[1],cp[1],end[1]);
          if(ex!==null)vertex.push([ex,last[1]]);
          if(ey!==null)vertex.push([last[0],ey]);
          last=end;segments++;
        }else if(cmd==='Z'&&first)last=first;
        else return null;
      }
    }catch(_){return null;}
    if(segments<3||!Number.isFinite(frame.width)||!Number.isFinite(frame.height))return null;
    const bounds=points=>({minX:Math.min(...points.map(v=>v[0])),maxX:Math.max(...points.map(v=>v[0])),minY:Math.min(...points.map(v=>v[1])),maxY:Math.max(...points.map(v=>v[1]))});
    // Iteration avoids JS argument caps for path lengths >10k nodes.
    const safeBounds=points=>{const b={minX:Infinity,maxX:-Infinity,minY:Infinity,maxY:-Infinity};for(const p of points){b.minX=Math.min(b.minX,p[0]);b.maxX=Math.max(b.maxX,p[0]);b.minY=Math.min(b.minY,p[1]);b.maxY=Math.max(b.maxY,p[1]);}return b;};
    const b=safeBounds(vertex),c=safeBounds(ctrl);
    const x0=Math.min(b.minX,c.minX),x1=Math.max(b.maxX,c.maxX),y0=Math.min(b.minY,c.minY),y1=Math.max(b.maxY,c.maxY);
    if(!(x1>x0)||!(y1>y0))return null;
    // Custom sticker zoom below FIT intentionally creates a smaller cut.
    // Fixed shapes keep the selected die-cut dimensions when artwork is zoomed.
    // Above FIT the editor retains its explicit CROP mode and paid-size limit.
    const fraction=Number(frame.cutFraction);
    if(!Number.isFinite(fraction)||fraction<.6||fraction>1)return null;
    // The final approved path is traced from raster alpha and expanded
    // one logical pixel for antialiasing. Reserve that allowance BEFORE
    // rendering the composite: full FIT must not overflow the paid frame.
    const margin=1.2,k=Math.min((frame.width-2*margin)/(x1-x0),(frame.height-2*margin)/(y1-y0))*fraction;
    if(!Number.isFinite(k)||k<=0||k>10)return null;
    const dx=frame.left+frame.width/2-k*(x0+x1)/2,dy=frame.top+frame.height/2-k*(y0+y1)/2;
    let index=0;
    const path=t.map(v=>{if(v==='M'||v==='Q'||v==='Z')return v;const val=Number(v)*k+(index++%2===0?dx:dy);return String(Number(val.toFixed(4)));}).join(' ');
    return {path,scale:k,dx,dy,widthIn:(b.maxX-b.minX)*k/frame.width*frame.widthIn,
      heightIn:(b.maxY-b.minY)*k/frame.height*frame.heightIn};
  }

  function setup(root) {
    if (root.dataset.sdStudioBound === 'true') return;
    const studio = root.querySelector('[data-sd-studio]');
    if (!studio) return;
    root.dataset.sdStudioBound = 'true';
    const canvas = studio.querySelector('[data-sd-studio-canvas]');
    const ctx = canvas.getContext('2d', { alpha: true });
    const cutlinePath = studio.querySelector('[data-sd-studio-cutline-path]');
    const workspaceLabel = studio.querySelector('[data-sd-studio-workspace-label]');
    const guideRect = studio.querySelector('[data-sd-studio-guide-rect]');
    const guideTicks = studio.querySelector('[data-sd-studio-guide-ticks]');
    const workspaceEmpty = studio.querySelector('[data-sd-studio-workspace-empty]');

    const workspace = studio.querySelector('[data-sd-studio-workspace]');
    // SD8 VISUAL QA: typography never overlaps rulers, art or contour.
    // Keep existing Liquid untouched. This presentation does not enter proofs.
    const measurementBar=document.createElement('div');
    measurementBar.setAttribute('data-sd8-measurement-bar','');
    measurementBar.setAttribute('aria-label','Finished sticker measurements and cutline key');
    Object.assign(measurementBar.style,{
      display:'flex',alignItems:'center',justifyContent:'space-between',
      flexWrap:'wrap',gap:'8px 16px',padding:'10px 13px',
      background:'#212125',color:'#F8F8F8',
      border:'1px solid #505056',borderBottom:'0',
      font:'600 12px/1.45 ui-monospace,SFMono-Regular,Menlo,monospace'
    });
    const measurementValue=document.createElement('span');
    measurementValue.style.cssText='display:inline-flex;align-items:baseline;gap:8px;flex-wrap:wrap';
    const measurementTitle=document.createElement('span');
    measurementTitle.textContent='FINISHED CUT:';
    measurementTitle.style.cssText='color:#C8C8CC;font-weight:700;letter-spacing:.01em';
    const headerCutSize=document.createElement('strong');
    headerCutSize.setAttribute('data-sd8-header-cut-size','');
    headerCutSize.textContent='Upload artwork';
    headerCutSize.style.cssText='font-size:14px;font-weight:800;color:#FFFFFF;white-space:nowrap';
    measurementValue.append(measurementTitle,headerCutSize);
    const cutlineLegend=document.createElement('span');
    cutlineLegend.style.cssText='display:inline-flex;align-items:center;gap:8px;color:#E4E4E7';
    const cutlineMark=document.createElement('span');
    cutlineMark.setAttribute('aria-hidden','true');
    cutlineMark.style.cssText='display:inline-block;width:25px;border-top:3px solid #FF00CF;flex:none';
    const cutlineDescription=document.createElement('span');
    cutlineDescription.textContent='PINK PATH · YOUR CUT SHAPE (PREVIEW)';
    cutlineLegend.append(cutlineMark,cutlineDescription);
    measurementBar.append(measurementValue,cutlineLegend);
    workspace.insertAdjacentElement('beforebegin',measurementBar);
    // Preserve existing hooks but prevent all three overlay text elements
    // from covering the scale and the customer's design.
    for(const selector of [
      '.sd-studio__workspace-label',
      '.sd-studio__workspace-empty',
      '.sd-studio__workspace-notice'
    ]){
      const overlay=studio.querySelector(selector);
      if(overlay)overlay.style.setProperty('display','none','important');
    }
    const canvasHint=studio.querySelector('.sd-studio__workspace + .sd-studio__hint');
    if(canvasHint)canvasHint.textContent='Pink line = cut preview.';
    const zoomHint=studio.querySelector('.sd-studio__fit-hint');
    if(zoomHint)zoomHint.textContent='Drag to position. Use FIT to reset.';
    const ruler=document.createElementNS('http://www.w3.org/2000/svg','svg');
    ruler.setAttribute('viewBox','0 0 600 500');
    ruler.setAttribute('preserveAspectRatio','none');
    ruler.setAttribute('aria-hidden','true');ruler.dataset.sd8Ruler='';
    Object.assign(ruler.style,{position:'absolute',inset:'0',width:'100%',height:'100%',zIndex:'5',pointerEvents:'none'});
    workspace.appendChild(ruler);
    const cutSize=document.createElement('p');
    cutSize.dataset.sd8FinalCutSize='';
    cutSize.setAttribute('role','status');
    Object.assign(cutSize.style,{padding:'10px 12px',margin:'9px 0 4px',
      background:'#FFFFFF',border:'1px solid #CFC9C1',color:'#191919',
      font:'700 12px/1.55 ui-monospace,monospace'});
    workspace.insertAdjacentElement('afterend',cutSize);
    const input = root.querySelector('[data-sd-artwork-input]');
    const openFileButton = studio.querySelector('[data-sd-studio-upload]');
    const range = studio.querySelector('[data-sd-studio-zoom]');
    const zoomValue = studio.querySelector('[data-sd-studio-zoom-value]');
    const zoomDown = studio.querySelector('[data-sd-studio-zoom-down]');
    const zoomUp = studio.querySelector('[data-sd-studio-zoom-up]');
    const zoomFit = studio.querySelector('[data-sd-studio-zoom-fit]');
    const cropWarning = studio.querySelector('[data-sd-studio-crop-warning]');
    const bgButton = studio.querySelector('[data-sd-studio-bg]');
    const reset = studio.querySelector('[data-sd-studio-reset]');
    const saveButton = studio.querySelector('[data-sd-studio-save]');
    const saveStatus = studio.querySelector('[data-sd-studio-save-status]');
    const uploadEnabled = root.querySelector('[data-sd-artwork]')?.dataset.uploadEnabled === 'true';
    const originalTokenInput = root.querySelector('[data-sd-artwork-token-input]');
    const apiBase = String(root.dataset.apiBase || '').replace(/\/+$/, '');
    const stateLine = studio.querySelector('[data-sd-studio-state]');
    const maxArea = studio.querySelector('[data-sd-studio-area]');
    // Size belongs in the two measurement bars, not as duplicate copy above.
    if(maxArea)maxArea.style.display='none';
    const unit = root.querySelector('[data-sd-studio-unit]');
    const summary = root.querySelector('[data-sd-studio-config-summary]');
    const note = root.querySelector('[data-sd-studio-cart-note]');
    const choices = Array.from(studio.querySelectorAll('[data-sd-border]'));
    let file = null, image = null, objectUrl = null, source = null, alphaBounds = null;
    let cleanedStrayCount=0;
    let backgroundCanvas=null, busyBackground=false, backgroundAbort=null;
    let backgroundRequest=0, backgroundFailed=false;
    const backgroundCopy={};
    for(const key of ['working','download','prepare','segment','success','failure','subject','unsupported','restoreFailure','restored','cancel','cancelled','reset']) {
      backgroundCopy[key]=bgButton.dataset[key]||'';
    }
    const backgroundRemoveLabel=bgButton.textContent;
    const backgroundRestoreLabel=bgButton.dataset.restoreLabel||backgroundRemoveLabel;
    let generation = 0, panX = 0, panY = 0, zoom = 1, background = false, border = 'standard';
    let latestRaw=null,latestFit=null;
    let quote = null, raf = null, dragging = false, lastX = 0, lastY = 0;
    let originalToken = null, savedEditSignature = null, revision = 0, busySaving = false;
    // High-density backing buffer. Logical editor coordinates (600×500) stay
    // unchanged for drag, pan and derivative exports, so no pricing/cart impact.
    function adjustCanvasResolution() {
      const dpr = Math.min(3, Math.max(2, window.devicePixelRatio || 1));
      const pixelW = Math.round(W*dpr), pixelH = Math.round(H*dpr);
      if (canvas.width!==pixelW || canvas.height!==pixelH) {
        canvas.width=pixelW;canvas.height=pixelH;
        ctx.setTransform(dpr,0,0,dpr,0,0);
        return true;
      }
      return false;
    }
    adjustCanvasResolution();
    const desktopStage = studio.parentElement;
    const mobileMount = root.querySelector('.sd-v2__artwork');
    const purchaseActions = studio.querySelector('.sd-studio__purchase-actions');
    const desktopPurchaseMount = purchaseActions?.parentElement;
    const mobilePurchaseMount = root.querySelector('.sd-studio__mobile-purchase');
    function moveForViewport() {
      const mobile = window.matchMedia('(max-width:900px)').matches;
      const destination = mobile && mobileMount ? mobileMount : desktopStage;
      if (studio.parentElement !== destination) destination.appendChild(studio);
      if (purchaseActions && desktopPurchaseMount) {
        const purchaseDestination = mobile && mobilePurchaseMount ? mobilePurchaseMount : desktopPurchaseMount;
        if (purchaseActions.parentElement !== purchaseDestination) purchaseDestination.appendChild(purchaseActions);
      }
    }
    window.addEventListener('resize', () => { if(adjustCanvasResolution()) schedule(); });
    window.addEventListener('resize', moveForViewport);
    moveForViewport();

    // One physical artwork region for ALL layers. Previous builds drew a fixed
    // 476x410 dotted guide but cropped to an invisible, narrower 407x350 frame.
    // That mismatch made CROP appear to stop before the advertised boundary.
    // The dotted guide now respects the selected width:height ratio (e.g. 3x3
    // is really square). Cropped pixels, cutline, and exported derivative share
    // this exact frame. FIT keeps 17px for the thickest preview-only border.
    const MAX_GUIDE_W = 476, MAX_GUIDE_H = 410, FIT_PAD = 17;
    const MAX_ZOOM_PERCENT = 170;
    const isCropping = () => zoom > 1.00001;
    function guideFrame() {
      let ratio = Number(quote?.width) / Number(quote?.height);
      if (!Number.isFinite(ratio) || ratio <= 0) ratio = 1;
      let width = MAX_GUIDE_W, height = MAX_GUIDE_W / ratio;
      if (height > MAX_GUIDE_H) {height = MAX_GUIDE_H; width = height * ratio;}
      return {left: (W-width)/2,top:(H-height)/2,width,height};
    }
    function safeFrame() {
      const f = guideFrame();
      return {left:f.left+FIT_PAD,top:f.top+FIT_PAD,width:f.width-2*FIT_PAD,height:f.height-2*FIT_PAD};
    }

    function drawRulers(f) {
      const width=Number(quote?.width),height=Number(quote?.height);
      if(!(width>0)||!(height>0)){ruler.replaceChildren();return;}
      // Two permanent gutters sit flush with the workspace edges, like a
      // physical ruler. The zero point follows the PAID guide coordinates,
      // not canvas-edge pixels (which are a virtual pasteboard).
      const horizontalBase=29,verticalBase=29;
      let html='<rect x="0" y="0" width="600" height="30" fill="#202126"/>'+
        '<rect x="0" y="0" width="30" height="500" fill="#202126"/>'+
        '<path d="M0 29H600 M29 0V500" fill="none" stroke="#666971" stroke-width=".8"/>'+
        '<text x="15" y="17" text-anchor="middle" fill="#DFFF00" font-size="10" font-family="monospace">in</text>';
      for(const axis of ['x','y']){
        const inches=axis==='x'?width:height,dist=axis==='x'?f.width:f.height;
        // Draw ticks at 1/4 inch with 1/2 inch text labels.
        const n=Math.floor(inches*4+1e-9);
        for(let j=0;j<=n;j++){
          const val=j/4,p=(axis==='x'?f.left:f.top)+dist*val/inches;
          const len=j%4===0?12:j%2===0?8:4;
          if(axis==='x'){
            html+='<path d="M'+p.toFixed(2)+' '+horizontalBase+'v-'+len+'" stroke="#DFFF00" stroke-width="1"/>';
            if(j%2===0)html+='<text x="'+p.toFixed(2)+'" y="12" text-anchor="middle" fill="#FFFFFF" font-size="12" font-family="monospace">'+val+'</text>';
          }else{
            html+='<path d="M'+verticalBase+' '+p.toFixed(2)+'h-'+len+'" stroke="#DFFF00" stroke-width="1"/>';
            if(j%2===0)html+='<text x="13" y="'+(p+3).toFixed(2)+'" text-anchor="middle" fill="#FFFFFF" font-size="12" font-family="monospace">'+val+'</text>';
          }
        }
      }
      ruler.innerHTML=html;
    }
    // Generate guide ticks from the same bounds used to clip the artwork.
    function updateGuides(f) {
      if (guideRect) {
        guideRect.setAttribute('x',String(f.left));
        guideRect.setAttribute('y',String(f.top));
        guideRect.setAttribute('width',String(f.width));
        guideRect.setAttribute('height',String(f.height));
      }
      // Measurement ticks are on the fixed outer rulers, not duplicated
      // around the dotted frame (that visual duplication hid the art edge).
      if(guideTicks)guideTicks.setAttribute('d','');
    }
    function actualOverflow(pos) {
      if(!pos || !source || !alphaBounds) return false;
      const f=guideFrame(), b=alphaBounds, scale=pos.width/source.width;
      return pos.x+b.left*scale<f.left-.1 || pos.x+b.right*scale>f.left+f.width+.1 ||
             pos.y+b.top*scale<f.top-.1 || pos.y+b.bottom*scale>f.top+f.height+.1;
    }
    function visibleAlphaBounds(src) {
      const w=src.width,h=src.height;
      const data=src.getContext('2d',{willReadFrequently:true}).getImageData(0,0,w,h).data;
      let minX=w,minY=h,maxX=-1,maxY=-1;
      for(let y=0;y<h;y++)for(let x=0;x<w;x++) {
        if(data[(y*w+x)*4+3]<120)continue;
        if(x<minX)minX=x;if(x>maxX)maxX=x;
        if(y<minY)minY=y;if(y>maxY)maxY=y;
      }
      if(maxX<minX)return {left:0,top:0,right:w,bottom:h};
      return {left:minX,top:minY,right:maxX+1,bottom:maxY+1};
    }
    function placement() {
      if(!source)return null;
      const b=alphaBounds||{left:0,top:0,right:source.width,bottom:source.height};
      const bw=Math.max(1,b.right-b.left),bh=Math.max(1,b.bottom-b.top);
      const fit=safeFrame();
      const base=Math.min(fit.width/bw,fit.height/bh);
      const size=base*zoom;
      const centerX=(b.left+b.right)/2,centerY=(b.top+b.bottom)/2;
      const bx=W/2-centerX*size,by=H/2-centerY*size;
      // FIT must retain the border inset. Above FIT, allow pixels to meet the
      // ACTUAL dotted boundary and clip there; never to an invisible inner box.
      const fr=isCropping()?guideFrame():fit;
      const xLeft=fr.left, xRight=fr.left+fr.width;
      const yTop=fr.top, yBottom=fr.top+fr.height;
      const artLeft=bx+b.left*size, artRight=bx+b.right*size;
      const artTop=by+b.top*size, artBottom=by+b.bottom*size;
      // At FIT and below, keep all pixels INSIDE the frame.
      // For intentional CROP, keep the frame covered by oversize artwork
      // while permitting drag to choose WHICH part is trimmed.
      const minPanX=(artRight-artLeft>fr.width ? xRight-artRight : xLeft-artLeft);
      const maxPanX=(artRight-artLeft>fr.width ? xLeft-artLeft : xRight-artRight);
      const minPanY=(artBottom-artTop>fr.height ? yBottom-artBottom : yTop-artTop);
      const maxPanY=(artBottom-artTop>fr.height ? yTop-artTop : yBottom-artBottom);
      panX=Math.max(minPanX,Math.min(maxPanX,panX));
      panY=Math.max(minPanY,Math.min(maxPanY,panY));
      return {x:bx+panX,y:by+panY,width:source.width*size,height:source.height*size};
    }
    function dirty() { return !!image && (background || border !== 'standard' || zoom !== 1 || Math.abs(panX) > 0.1 || Math.abs(panY) > 0.1); }
    function editSpec() {
      return {
        border, zoom: Number(zoom.toFixed(3)), pan_x: Number(panX.toFixed(2)),
        pan_y: Number(panY.toFixed(2)), remove_white_background: background,
        shape: String(quote?.shape || 'custom'), cut_style: String(quote?.cut_style || 'die_cut'),
        width: Number(quote?.width || 0), height: Number(quote?.height || 0),
        rendering: 'flattened_transparent_png_cutsize_v2'
      };
    }
    function currentSignature() { return JSON.stringify(editSpec()); }
    function isUnsaved() { return !!image && (dirty() || !!savedEditSignature) && currentSignature() !== savedEditSignature; }
    function activateOriginal() {
      if (!originalToken) return;
      root.dataset.sdArtworkToken = originalToken;
      if (originalTokenInput) originalTokenInput.value = originalToken;
    }
    function onChange() {
      if(source)placement(); // Clamp any drag immediately, before saving a manifest.
      revision += 1;
      if (!dirty()) activateOriginal();
      sync(); schedule();
    }
    function chooseFile() {
      if (!input) {
        stateLine.textContent = 'Artwork selection is unavailable. Reload the page or contact support.';
        return;
      }
      input.click();
    }
    function sync() {
      const isDirty = isUnsaved() || busyBackground || backgroundFailed;
      canvas.classList.toggle('is-empty', !image);
      if (workspaceEmpty) workspaceEmpty.hidden = !!image;
      if (workspace) workspace.classList.toggle('has-design', !!image);
      canvas.tabIndex = image ? -1 : 0;
      canvas.setAttribute('role', image ? 'img' : 'button');
      canvas.setAttribute('aria-label', image ? 'Artwork preview, drag to reposition image' : 'Click or press Enter to choose an artwork file');
      if (openFileButton) openFileButton.firstChild.textContent = image ? 'REPLACE FILE ' : 'UPLOAD DESIGN ';
      root.dataset.sdStudioNeedsSave = isDirty ? 'true' : 'false';
      const hasOverflow = isCropping() && actualOverflow(placement());
      stateLine.textContent = !image ? '' :
        hasOverflow ? '' :
        isDirty ? 'Save your changes before approving.' :
        '';
      stateLine.dataset.unsaved = isDirty ? 'true' : 'false';
      if (saveButton) {
        saveButton.hidden = !isUnsaved();
        saveButton.disabled = !uploadEnabled || !originalToken || !quote || !Number(quote.width) || !Number(quote.height) || busySaving || busyBackground || backgroundFailed || !isDirty ||
          studio.dataset.sdFinalAlignment==='hold';
      }
      if (saveStatus && !uploadEnabled) saveStatus.textContent = 'Saving is unavailable. Please try again later.';
      if (note) note.textContent = isDirty ?
        '' :
        '';
      root.dispatchEvent(new CustomEvent('sd:studio-changed', { bubbles: true, detail: { unsaved: isDirty } }));
      const pct=Math.round(zoom * 100);
      zoomValue.textContent = pct + '%' + (pct===100?' FIT':hasOverflow?' CROP':' ZOOM');
      range.value = String(pct);
      if (cropWarning) {
        cropWarning.hidden = !image || !hasOverflow;
        if (hasOverflow && String(quote?.shape||'custom')==='custom') {
          cropWarning.textContent='Part of your custom outline will be cropped. Use FIT to keep it all.';
        } else if (hasOverflow) {
          cropWarning.textContent='Part of your design will be cropped. Use FIT to keep it all.';
        }
      }
      if(zoomDown) zoomDown.disabled = !image || busyBackground || pct<=60;
      if(zoomUp) zoomUp.disabled = !image || busyBackground || pct>=MAX_ZOOM_PERCENT;
      if(zoomFit) zoomFit.disabled = !image || busyBackground || (pct===100 && Math.abs(panX)<.1 && Math.abs(panY)<.1);
      range.disabled=busyBackground;
      bgButton.disabled=!image||busySaving||busyBackground;
      bgButton.textContent=busyBackground?backgroundCopy.working:background?backgroundRestoreLabel:backgroundRemoveLabel;
      bgButton.setAttribute('aria-busy',String(busyBackground));
      reset.disabled=busySaving;
      reset.textContent=busyBackground?backgroundCopy.cancel:backgroundCopy.reset;
      studio.setAttribute('aria-busy',String(busyBackground));
      bgButton.setAttribute('aria-pressed', String(background));
      bgButton.classList.toggle('is-selected', background);
      choices.forEach(button => {
        button.disabled=busyBackground||busySaving;
        const active = button.dataset.sdBorder === border;
        button.setAttribute('aria-pressed', String(active));
        button.classList.toggle('is-selected', active);
      });
    }

    // SD8.2 QA: refuse contours that cross visible artwork after smoothing.
    // Compare raster alpha against an independent vector Path2D rasterization.
    // The same vetted path drives the canvas border, SVG capture and PDF.
    const cutlineAudit=document.createElement('canvas');
    cutlineAudit.width=W;cutlineAudit.height=H;
    const cutlineAuditCtx=cutlineAudit.getContext('2d',{willReadFrequently:true});
    function preservesArtwork(path,inkMask,scale){
      if(!path||!inkMask||typeof Path2D!=='function'||!cutlineAuditCtx||
         !Number.isFinite(scale)||scale<=0)return false;
      const strokePixels=2.35*Math.min(1,scale);
      // Preview stroke (pink) and a small separation allowance, converted
      // into the PRE-normalization coordinates for a fair collision test.
      const guardWidth=(strokePixels+1.4)/scale;
      let svg;
      try{svg=new Path2D(path);}catch(_){return false;}
      cutlineAuditCtx.setTransform(1,0,0,1,0,0);
      cutlineAuditCtx.clearRect(0,0,W,H);
      cutlineAuditCtx.fillStyle='#FFFFFF';
      cutlineAuditCtx.fill(svg);
      const fill=cutlineAuditCtx.getImageData(0,0,W,H).data;
      cutlineAuditCtx.clearRect(0,0,W,H);
      cutlineAuditCtx.lineWidth=guardWidth;
      cutlineAuditCtx.lineCap='round';cutlineAuditCtx.lineJoin='round';
      cutlineAuditCtx.strokeStyle='#FFFFFF';
      cutlineAuditCtx.stroke(svg);
      const edge=cutlineAuditCtx.getImageData(0,0,W,H).data;
      for(let i=0;i<inkMask.length;i++){
        if(!inkMask[i])continue;
        // No visible content may lie outside cut area or within stroke margin.
        if(fill[i*4+3]<245||edge[i*4+3]>20)return false;
      }
      return true;
    }

    // SD8.4 QA: final-on-screen alpha vs captured SVG alignment gate.
    // This detects a mismatch AFTER all transforms (unlike the pre-fit mask
    // clearance audit). It is a safety check, not an automatic geometry repair.
    const finalAuditCanvas=document.createElement('canvas');
    finalAuditCanvas.width=W;finalAuditCanvas.height=H;
    const finalAuditCtx=finalAuditCanvas.getContext('2d',{willReadFrequently:true});
    function auditFinalArtworkAlignment(path){
      if(!path||typeof Path2D!=='function'||!finalAuditCtx)return {ok:false,outside:-1};
      try{
        // Actual pixels already displayed by the physical high-DPI canvas.
        finalAuditCtx.setTransform(1,0,0,1,0,0);
        finalAuditCtx.clearRect(0,0,W,H);
        finalAuditCtx.drawImage(canvas,0,0,W,H);
        const pixels=finalAuditCtx.getImageData(0,0,W,H).data;
        finalAuditCtx.clearRect(0,0,W,H);
        const contour=new Path2D(path);
        finalAuditCtx.fillStyle='#FFFFFF';
        finalAuditCtx.fill(contour);
        finalAuditCtx.lineWidth=4;
        finalAuditCtx.lineJoin='round';
        finalAuditCtx.lineCap='round';
        finalAuditCtx.strokeStyle='#FFFFFF';
        finalAuditCtx.stroke(contour);
        const allowed=finalAuditCtx.getImageData(0,0,W,H).data;
        let outside=0;
        for(let i=0;i<W*H;i++){
          if(pixels[i*4+3]>=100 && allowed[i*4+3]<40)outside++;
        }
        const canvasRect=canvas.getBoundingClientRect();
        const svgRect=cutlinePath?.ownerSVGElement?.getBoundingClientRect();
        const rectsMatch=!!svgRect&&Math.abs(canvasRect.left-svgRect.left)<1.5&&
          Math.abs(canvasRect.top-svgRect.top)<1.5&&
          Math.abs(canvasRect.width-svgRect.width)<1.5&&
          Math.abs(canvasRect.height-svgRect.height)<1.5;
        return {ok:outside<=15&&rectsMatch,outside,rectsMatch};
      }catch(_){return {ok:false,outside:-1,rectsMatch:false};}
    }
    function render() {
      raf = null;latestRaw=null;latestFit=null;
      ctx.clearRect(0,0,W,H);
      const guide=guideFrame();
      updateGuides(guide);drawRulers(guide);
      cutSize.textContent='';
      headerCutSize.textContent='Upload artwork';
      if (workspaceLabel) {
        workspaceLabel.textContent='MAX ARTWORK AREA · '+(quote?.width || '—')+' × '+(quote?.height || '—')+' IN';
      }
      if (cutlinePath) cutlinePath.setAttribute('d','');
      if (!image || !source) return;
      const raw = document.createElement('canvas');raw.width=W;raw.height=H;
      const c = raw.getContext('2d',{willReadFrequently:true});
      const pos=placement();
      const shape=String(quote?.shape||'custom');
      const fixed=['circle','oval','square','rectangle'].includes(shape);
      // Fixed shapes stay inset by the same FIT border margin so the visual
      // cutline does not falsely extend beyond the displayed print boundary.
      const inside=safeFrame();
      let pw=inside.width,ph=inside.height;
      if (fixed && (shape==='circle'||shape==='square')) pw=ph=Math.min(pw,ph);
      const left=(W-pw)/2, top=(H-ph)/2;
      function pathShape() {
        c.beginPath();
        if(shape==='circle'||shape==='oval')c.ellipse(W/2,H/2,pw/2,ph/2,0,0,Math.PI*2);
        else c.rect(left,top,pw,ph);
      }
      c.save();
      if(isCropping()){
        c.beginPath();c.rect(guide.left,guide.top,guide.width,guide.height);c.clip();
      }
      if(fixed){pathShape();c.clip();}
      c.drawImage(source,pos.x,pos.y,pos.width,pos.height);
      c.restore();
      const data=c.getImageData(0,0,W,H);
      const mask=new Uint8Array(W*H);
      if(!fixed){
        // Include low-opacity visible artwork fringe in the OUTER cut mask.
        // Previous >120 threshold discarded anti-aliased edge pixels and could
        // let the pink contour intersect colors after changing border/size.
        // Keep the same source raster in the border, cutline and HMAC SVG.
        for(let i=0;i<mask.length;i++)mask[i]=data.data[i*4+3]>=16?1:0;
      }else{
        for(let y=0;y<H;y++)for(let x=0;x<W;x++) {
          const inside=(shape==='circle'||shape==='oval')?
            ((x-W/2)**2/(pw/2)**2+(y-H/2)**2/(ph/2)**2<=1):
            (x>=left&&x<=left+pw&&y>=top&&y<=top+ph);
          if(inside)mask[y*W+x]=1;
        }
      }
      const radius=border==='thin'?3:border==='thick'?13:7;
      const white=dilate(mask,W,H,radius);
      const whitePixels=c.createImageData(W,H);
      for(let i=0;i<white.length;i++)if(white[i]) {
        // The white border and its magenta contour MUST stop at the very same
        // dotted rectangle as the edited PNG export, not at a second boundary.
        const x=i%W, y=Math.floor(i/W);
        if (isCropping() && (x<guide.left || x>=guide.left+guide.width || y<guide.top || y>=guide.top+guide.height)) {
          white[i]=0; continue;
        }
        const p=i*4;
        whitePixels.data[p]=255;whitePixels.data[p+1]=255;
        whitePixels.data[p+2]=255;whitePixels.data[p+3]=255;
      }
      const whiteLayer=document.createElement('canvas');whiteLayer.width=W;whiteLayer.height=H;
      whiteLayer.getContext('2d').putImageData(whitePixels,0,0);
      // Retina bitmap for the user art & white vinyl border. Guides and cutline
      // remain independent DOM/SVG layers, not bitmap text at low resolution.
      const mediumEnabled = shape==='custom' && !isCropping();
      const mediumTolerance = .00185*Math.min(guide.width,guide.height);
      const smoothedPath = vectorOutlines(white,W,H,mediumEnabled,mediumTolerance);
      // The raw artwork already includes the selected zoom. Fit it to the
      // physical guide ONCE, then apply the requested finished-size ratio
      // to the complete printable composite in a calibrated final pass.
      const targetFinishedFraction=shape==='custom'&&zoom<1?zoom:1;
      const sizeFraction=1;
      // Explicit quote size change invalidates ALL geometry, not just the
      // measurement label. Render regenerates selected raster, visible mask,
      // white underlay, vector and the captured approval path atomically.
      // When smoothing moves the pink curve into the ink, prefer a more
      // conservative geometry. Never silently commit an unsafe vector path.
      let approvedPath=smoothedPath;
      let cutlineUnsafe=false;
      if(shape==='custom'&&!isCropping()){
        const trialFit=fitContourToPaidSize(approvedPath,{left:guide.left,top:guide.top,
          width:guide.width,height:guide.height,widthIn:Number(quote?.width),
          heightIn:Number(quote?.height),cutFraction:sizeFraction});
        if(!trialFit||!preservesArtwork(approvedPath,mask,trialFit.scale)){
          approvedPath=vectorOutlines(white,W,H,false,0);
          const safeFit=fitContourToPaidSize(approvedPath,{left:guide.left,top:guide.top,
            width:guide.width,height:guide.height,widthIn:Number(quote?.width),
            heightIn:Number(quote?.height),cutFraction:sizeFraction});
          if(!safeFit||!preservesArtwork(approvedPath,mask,safeFit.scale)){
            approvedPath='';cutlineUnsafe=true;
          }
        }
      }
      // SD8 QA: trim size is computed from APPROVED outer cutline, never PDF box.
      const fit=approvedPath ? fitContourToPaidSize(approvedPath,{left:guide.left,top:guide.top,
        width:guide.width,height:guide.height,widthIn:Number(quote?.width),
        heightIn:Number(quote?.height),cutFraction:sizeFraction}):null;
      if(cutlineUnsafe){
        cutSize.textContent='Artwork is too close to the cut edge. Choose STANDARD or THICK border.';
        headerCutSize.textContent='CUT PATH NEEDS REVIEW';
      }
      latestRaw=raw;latestFit=fit;
      function paintApprovedArtwork() {
        ctx.clearRect(0,0,W,H);
        if(!fit)return;
        ctx.save();
        ctx.translate(fit.dx,fit.dy);
        ctx.scale(fit.scale,fit.scale);
        ctx.imageSmoothingEnabled=true;ctx.imageSmoothingQuality='high';
        if(mediumEnabled&&approvedPath&&typeof Path2D==='function'){
          try{
            ctx.fillStyle='#fff';
            ctx.fill(new Path2D(approvedPath));
          }catch(_){ctx.drawImage(whiteLayer,0,0,W,H);}
        }else ctx.drawImage(whiteLayer,0,0,W,H);
        ctx.drawImage(raw,0,0,W,H);
        ctx.restore();
      }
      // The physical cut is traced from the final visible composite.
      function readCompositeContour(){
        finalAuditCtx.setTransform(1,0,0,1,0,0);
        finalAuditCtx.clearRect(0,0,W,H);
        finalAuditCtx.drawImage(canvas,0,0,W,H);
        const pixels=finalAuditCtx.getImageData(0,0,W,H).data;
        const visible=new Uint8Array(W*H);
        let count=0;
        for(let i=0;i<visible.length;i++){
          if(pixels[i*4+3]>=16){visible[i]=1;count++;}
        }
        if(count<50)return null;
        const path=vectorOutlines(dilate(visible,W,H,1),W,H,false,0);
        if(!path||!cutlinePath)return null;
        cutlinePath.setAttribute('d',path);
        const bounds=cutlinePath.getBBox();
        return Number.isFinite(bounds.x)&&bounds.width>0&&bounds.height>0?{path,bounds}:null;
      }
      let finalContour=null,finalCutSize=null;
      if(fit&&finalAuditCtx){
        try{
          paintApprovedArtwork();
          const preview=readCompositeContour();
          if(preview){
            const b=preview.bounds;
            const shrinkGoal=Math.min(1,Math.max(.6,targetFinishedFraction));
            // Real outer cut dimensions, including the selected white
            // border, are the commercial size. Keep a 0.4px digital
            // tolerance inside the quoted maximum, at every selected size.
            const desiredW=guide.width*shrinkGoal-.4;
            const desiredH=guide.height*shrinkGoal-.4;
            const correction=Math.min(desiredW/b.width,desiredH/b.height);
            const gcx=guide.left+guide.width/2,gcy=guide.top+guide.height/2;
            const shiftX=gcx-correction*(b.x+b.width/2);
            const shiftY=gcy-correction*(b.y+b.height/2);
            if(Number.isFinite(correction)&&correction>.55&&correction<1.2&&
               Number.isFinite(shiftX)&&Number.isFinite(shiftY)){
              // Crucial: change the SINGLE fit used by the preview, saved
              // selected PNG, white border, and signed SVG. Scaling only
              // the pink line would reintroduce the earlier intrusion bug.
              fit.dx=fit.dx*correction+shiftX;
              fit.dy=fit.dy*correction+shiftY;
              fit.scale*=correction;
              paintApprovedArtwork();
              const final=readCompositeContour();
              if(final){
                const p=final.bounds,t=.5;
                if(p.x>=guide.left-t&&p.y>=guide.top-t&&
                   p.x+p.width<=guide.left+guide.width+t&&
                   p.y+p.height<=guide.top+guide.height+t&&
                   p.width<=guide.width*shrinkGoal+.5&&
                   p.height<=guide.height*shrinkGoal+.5){
                  finalContour=final.path;
                  finalCutSize={
                    width:p.width/guide.width*Number(quote.width),
                    height:p.height/guide.height*Number(quote.height)
                  };
                }
              }
            }
          }
        }catch(_){finalContour=null;finalCutSize=null;}
      }
      const finalAudit=finalCutSize?auditFinalArtworkAlignment(finalContour):{ok:false,outside:-1};
      studio.dataset.sdFinalAlignment=finalAudit.ok?'pass':'hold';
      if(fit&&!finalAudit.ok){
        latestFit=null; // saveEditedVersion fails closed BEFORE any upload.
        const suffix=finalAudit.outside>=0?' ('+finalAudit.outside+' pixels outside cut path)':'';
        // Do not misreport a bounds failure as a visual-alignment failure.
        const name=finalContour&&!finalCutSize?'CUT SIZE HOLD':'CUTLINE ALIGNMENT HOLD';
        const explanation=name==='CUT SIZE HOLD'
          ?'final cut contour exceeds the selected maximum size'
          :'outline and visible artwork differ';
        cutSize.textContent=name+' — '+explanation+suffix+'. Do not approve.';
        headerCutSize.textContent=name;
      }
      if(finalAudit.ok){
        cutSize.textContent='Cut size: '+finalCutSize.width.toFixed(3)+' × '+finalCutSize.height.toFixed(3)+' in'+
          (shape==='custom'&&zoom<.999?' · SMALLER THAN ORDERED MAXIMUM':'');
        headerCutSize.textContent=finalCutSize.width.toFixed(2)+'" × '+finalCutSize.height.toFixed(2)+'"';
      }
      // Re-evaluate the save action AFTER cut-size verification. sync()
      // ran before requestAnimationFrame(render), so switching dimensions
      // after a HOLD could leave the button disabled even when geometry PASSES.
      if(saveButton){
        const unsavedNow=isUnsaved();
        saveButton.hidden=!unsavedNow;
        saveButton.disabled=!uploadEnabled||!originalToken||!quote||
          !Number(quote.width)||!Number(quote.height)||
          busySaving||busyBackground||backgroundFailed||!unsavedNow||studio.dataset.sdFinalAlignment!=='pass';
      }
      if(cutlinePath){
        cutlinePath.setAttribute('d',finalContour||'');
        // Keep the fixed visual stroke from overwhelming thin/zoomed borders.
        cutlinePath.setAttribute('stroke-width',String((2.35*Math.min(1,fit?.scale||1)).toFixed(3)));
      }
    }
    function schedule() { if (raf === null) raf = requestAnimationFrame(render); }
    function stopBackgroundRequest() {
      backgroundRequest++;
      backgroundAbort?.abort();
      backgroundAbort=null;
      busyBackground=false;
    }
    function originalCanvas() {
      const scale=Math.min(1,MAX_SOURCE/Math.max(image.naturalWidth,image.naturalHeight));
      const next=document.createElement('canvas');
      next.width=Math.max(1,Math.round(image.naturalWidth*scale));
      next.height=Math.max(1,Math.round(image.naturalHeight*scale));
      next.getContext('2d').drawImage(image,0,0,next.width,next.height);
      return next;
    }
    async function removeBackground(restore=false) {
      if(!image||busySaving||busyBackground)return false;
      const request=++backgroundRequest,atGeneration=generation;
      backgroundAbort=new AbortController();
      const signal=backgroundAbort.signal;
      busyBackground=true;dragging=false;backgroundFailed=false;
      onChange();
      if(saveStatus)saveStatus.textContent=backgroundCopy.prepare;
      try {
        let resultCanvas=originalCanvas();
        const legacy=edgeBackgroundRemoved(resultCanvas);
        if(!legacy.ok) {
          if(!window.SDPhotoBackground)throw Error('browser_unsupported');
          resultCanvas=await window.SDPhotoBackground.remove(resultCanvas,{
            signal,
            onProgress:(phase,percent)=>{
              if(request!==backgroundRequest||atGeneration!==generation)return;
              if(saveStatus)saveStatus.textContent=(backgroundCopy[phase]||backgroundCopy.segment).replace('{percent}',String(percent||0));
            }
          });
        }
        if(signal.aborted||request!==backgroundRequest||atGeneration!==generation)return false;
        backgroundCanvas=resultCanvas;background=true;
        cleanedStrayCount=legacy.ok?(legacy.cleaned||0):0;
        if(!restore){zoom=1;panX=panY=0;}
        rebuildSource();onChange();
        if(saveStatus)saveStatus.textContent=backgroundCopy.success;
        return true;
      } catch(error) {
        if(request!==backgroundRequest||atGeneration!==generation)return false;
        background=false;backgroundCanvas=null;
        backgroundFailed=true;
        rebuildSource();onChange();
        if(saveStatus)saveStatus.textContent=error?.name==='AbortError'?backgroundCopy.cancelled:
          restore?backgroundCopy.restoreFailure:
          error?.message==='subject_not_found'?backgroundCopy.subject:
          error?.message==='browser_unsupported'?backgroundCopy.unsupported:backgroundCopy.failure;
        return false;
      } finally {
        if(request===backgroundRequest&&atGeneration===generation){
          backgroundAbort=null;busyBackground=false;sync();schedule();
        }
      }
    }
    function setFile(next, restore = null) {
      stopBackgroundRequest();
      const id=++generation;
      if(objectUrl){URL.revokeObjectURL(objectUrl);objectUrl=null;}
      file=next&&allowed.test(next.name)?next:null;
      originalToken=null;savedEditSignature=null;revision+=1;
      backgroundCanvas=null;backgroundFailed=false;
      if(saveStatus)saveStatus.textContent='';
      image=null;source=null;alphaBounds=null;panX=panY=0;zoom=1;background=false;border='standard';
      if(!file){sync();schedule();return;}
      objectUrl=URL.createObjectURL(file);
      const nextImage=new Image();
      nextImage.onload=async()=>{
        if(id!==generation)return;
        image=nextImage;
        let restoreBackground=false;
        if(restore){
          const s=restore.spec||{};
          border=['thin','standard','thick'].includes(s.border)?s.border:'standard';
          restoreBackground=s.remove_white_background===true;
          zoom=Number.isFinite(s.zoom)&&s.zoom>=.6&&s.zoom<=1.7?s.zoom:1;
          panX=Number.isFinite(s.pan_x)?s.pan_x:0;
          panY=Number.isFinite(s.pan_y)?s.pan_y:0;
          originalToken=restore.original_token;
          savedEditSignature=restore.spec?JSON.stringify({border:s.border,zoom:s.zoom,pan_x:s.pan_x,pan_y:s.pan_y,remove_white_background:s.remove_white_background,shape:s.shape,cut_style:s.cut_style,width:s.width,height:s.height,rendering:s.rendering}):null;
        }
        rebuildSource();sync();schedule();
        if(restoreBackground)await removeBackground(true);
        if(id!==generation)return;
        if(restore){
          root.dataset.sdArtworkToken=restore.active_token;
          if(originalTokenInput)originalTokenInput.value=restore.active_token;
        }
        sync();schedule();
      };
      nextImage.onerror=()=>{if(id!==generation)return;file=null;image=null;sync();schedule();};
      nextImage.src=objectUrl;
    }
    function rebuildSource() {
      if(!image)return;
      source=background&&backgroundCanvas?backgroundCanvas:originalCanvas();
      alphaBounds=visibleAlphaBounds(source);
      placement();
    }
    function clear() { setFile(null); }
    function resetEdits() {
      if(busyBackground){stopBackgroundRequest();onChange();if(saveStatus)saveStatus.textContent=backgroundCopy.cancelled;return;}
      backgroundFailed=false;
      panX = panY = 0;zoom = 1;border = 'standard';background = false;
      // Explicit reset chooses the immutable original, not the restored derivative.
      savedEditSignature = null;
      if (image) rebuildSource();onChange();
    }
    root.addEventListener('sd:cart-edit-restore-artwork', event => {
      const d=event.detail||{};
      if (d.file instanceof File && /^[A-Za-z0-9_-]{32,128}$/.test(d.original_token||'') &&
          /^[A-Za-z0-9_-]{32,128}$/.test(d.active_token||'')) {
        setFile(d.file,{spec:d.spec||null,original_token:d.original_token,active_token:d.active_token});
      }
    });
    root.addEventListener('sd:artwork-selected', event => { setFile(event.detail?.file || null); });
    root.addEventListener('sd:artwork-cleared', clear);
    root.addEventListener('sd:artwork-uploaded', event => {
      if (!event.detail?.derived && typeof event.detail?.info?.token === 'string') {
        originalToken = event.detail.info.token;
        sync();
      }
    });
    root.addEventListener('sd:quote-loading', () => { quote = null;if (unit) unit.textContent = '';if (summary) summary.textContent = 'Recalculating…'; schedule(); });
    root.addEventListener('sd:quote-error', () => { quote = null;if (unit) unit.textContent = '';if (summary) summary.textContent = 'Quote unavailable'; schedule(); });
    root.addEventListener('sd:quote-ready', event => {
      const detail = event.detail || {}, cfg = detail.configuration || {}, q = detail.quote || {};
      quote = cfg;
      maxArea.textContent = `${cfg.width || '—'} × ${cfg.height || '—'} in · maximum artwork area`;
      if (summary) summary.textContent = [cfg.quantity ? `${Number(cfg.quantity).toLocaleString('en-US')} stickers` : '',
        cfg.width && cfg.height ? `${cfg.width} × ${cfg.height} in` : '',
        String(cfg.shape || '').replace(/_/g, ' '), String(cfg.cut_style || '').replace(/_/g, ' '), cfg.finish].filter(Boolean).join(' · ');
      const total = Number(q.total_price), qty = Number(cfg.quantity);
      if (unit) unit.textContent = q.status === 'priced' && Number.isFinite(total) && qty > 0 ? `${money(total / qty)} each` : '';
      // A saved derivative is usable only for EXACT parameters it was created with.
      if (image && savedEditSignature && dirty() && currentSignature() !== savedEditSignature) {
        root.dataset.sdStudioNeedsSave='true';
      }
      sync();schedule();
    });
    // The editor itself must open the same validated original artwork uploader.
    // Without this, the canvas looked clickable but did nothing in v4.7.
    if (openFileButton) openFileButton.addEventListener('click', chooseFile);
    canvas.addEventListener('click', () => { if (!image) chooseFile(); });
    canvas.addEventListener('keydown', e => {
      if (!image && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); chooseFile(); }
    });
    canvas.addEventListener('dragover', e => { if (!image) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; } });
    canvas.addEventListener('drop', e => {
      if (!input || !e.dataTransfer) return;
      e.preventDefault();
      if (e.dataTransfer.files.length !== 1) {
        stateLine.textContent = 'Choose one design file at a time.';
        return;
      }
      try {
        input.files = e.dataTransfer.files;
        input.dispatchEvent(new Event('change', {bubbles:true}));
      } catch (_) {
        stateLine.textContent = 'Use UPLOAD DESIGN to select your artwork.';
      }
    });
    function setZoom(pct) {
      const next=Math.max(60,Math.min(MAX_ZOOM_PERCENT,Math.round(Number(pct))));
      if(!image||next===Math.round(zoom*100))return;
      zoom=next/100;onChange();
    }
    range.addEventListener('input', () => setZoom(range.value));
    if(zoomDown)zoomDown.addEventListener('click',()=>setZoom(Math.round(zoom*100)-1));
    if(zoomUp)zoomUp.addEventListener('click',()=>setZoom(Math.round(zoom*100)+1));
    if(zoomFit)zoomFit.addEventListener('click',()=>{panX=panY=0;zoom=1;onChange();});
    choices.forEach(button => button.addEventListener('click', () => { border = button.dataset.sdBorder;onChange(); }));
    bgButton.addEventListener('click', () => {
      if(!image||busyBackground||busySaving)return;
      if(background){
        background=false;backgroundFailed=false;
        rebuildSource();onChange();
        if(saveStatus)saveStatus.textContent=backgroundCopy.restored;
      }else if(backgroundCanvas){
        background=true;backgroundFailed=false;zoom=1;panX=panY=0;
        rebuildSource();onChange();
        if(saveStatus)saveStatus.textContent=backgroundCopy.success;
      }else void removeBackground(false);
    });
    reset.addEventListener('click', resetEdits);
    canvas.addEventListener('pointerdown', e => { if (!image||busyBackground) return;dragging = true;lastX = e.clientX;lastY = e.clientY;canvas.setPointerCapture(e.pointerId); });
    canvas.addEventListener('pointermove', e => {
      if (!dragging || !image) return;
      const rect = canvas.getBoundingClientRect();
      panX += (e.clientX - lastX) * (W / rect.width);panY += (e.clientY - lastY) * (H / rect.height);
      lastX = e.clientX;lastY = e.clientY;onChange();
    });
    canvas.addEventListener('pointerup', () => { dragging = false; });
    canvas.addEventListener('pointercancel', () => { dragging = false; });
    async function saveEditedVersion(force = false) {
      if (!uploadEnabled || !originalToken || !source || (!isUnsaved() && !force) || busySaving || busyBackground || backgroundFailed) return;
      if (!quote || !Number(quote.width) || !Number(quote.height)) return;
      const parent = originalToken;
      const spec = editSpec();
      const signature = JSON.stringify(spec), atRevision = revision;
      const geometryRequired = root.dataset.sdGeometryCaptureEnabled === 'true';
      busySaving = true;sync();
      if (saveStatus) saveStatus.textContent = 'Saving your design…';
      try {
        render();
        if(!latestRaw||!latestFit)throw Error('Finished cut size could not be verified.');
        const capturedPath = geometryRequired ? String(cutlinePath?.getAttribute('d') || '') : '';
        if (geometryRequired && !capturedPath) throw Error('The cutline preview is empty. Please review the artwork.');
        // Export image pixels, NOT guide marks, simulated borders or magenta trim line.
        // Preserve user frame/zoom as an independent transparent PNG for production review.
        const output = document.createElement('canvas');
        const f=guideFrame();
        // Preserve the real width:height artwork ratio instead of stretching
        // a fixed 445x388 canvas into a square derivative on export.
        if(f.width>=f.height){output.width=1200;output.height=Math.max(1,Math.round(1200*f.height/f.width));}
        else{output.height=1200;output.width=Math.max(1,Math.round(1200*f.width/f.height));}
        // Export the SAME clipped raster that produced the visible preview.
        const context=output.getContext('2d');
        const sx=output.width/f.width,sy=output.height/f.height;
        context.setTransform(sx*latestFit.scale,0,0,sy*latestFit.scale,
          (latestFit.dx-f.left)*sx,(latestFit.dy-f.top)*sy);
        context.drawImage(latestRaw,0,0);
        context.setTransform(1,0,0,1,0,0);
        const blob = await new Promise((resolve,reject) => output.toBlob(b => b ? resolve(b) : reject(Error('PNG export failed.')),'image/png'));
        if (blob.size > 25*1024*1024) throw new Error('Edited PNG is too large.');
        const form = new FormData();form.append('artwork',blob,'edited-design.png');
        const response = await fetch(apiBase + '/?sd_route=artwork/derived/upload', {
          method:'POST',body:form,mode:'cors',credentials:'omit',cache:'no-store',referrerPolicy:'no-referrer',
          headers:{'X-SD-Original-Token':parent,'X-SD-Edit-Spec':signature}
        });
        const result = await response.json();
        if (!response.ok || result.ok!==true || result.status!=='derived_saved' || !result.parent_recorded ||
            !/^[A-Za-z0-9_-]{32,128}$/.test(result.artwork?.token || '')) {
          throw new Error(result.message || 'Edited PNG was not saved.');
        }
        if (geometryRequired) {
          const capturedFrame = guideFrame();
          const capture = {
            version: 'sd-geometry-v1',
            selected_token: result.artwork.token,
            original_token: parent,
            edit_spec: spec,
            cutline_path: capturedPath,
            frame: {x: capturedFrame.left, y: capturedFrame.top,
                    width: capturedFrame.width, height: capturedFrame.height}
          };
          const geomResponse = await fetch(apiBase + '/?sd_route=artwork/derived/geometry', {
            method: 'POST', mode:'cors', credentials:'omit', cache:'no-store',
            referrerPolicy:'no-referrer', headers: {'Content-Type':'application/json'},
            body: JSON.stringify(capture)
          });
          const geomResult = await geomResponse.json();
          if (!geomResponse.ok || geomResult.ok !== true ||
              !/^[a-f0-9]{64}$/.test(String(geomResult.capture_sha256 || ''))) {
            throw Error('The approved cutline could not be saved. Please retry before approval.');
          }
          root.dataset.sdGeometryCapturedToken = result.artwork.token;
          root.dataset.sdGeometryCaptureHash = geomResult.capture_sha256;
        }
        if (atRevision!==revision || parent!==originalToken) {
          if (saveStatus) saveStatus.textContent = 'Design changed while saving. Save the latest version again.';
          return;
        }
        savedEditSignature = signature;
        root.dataset.sdArtworkToken = result.artwork.token;
        if (originalTokenInput) originalTokenInput.value = result.artwork.token;
        root.dispatchEvent(new CustomEvent('sd:artwork-uploaded',{bubbles:true,detail:{derived:true, info:result.artwork}}));
        if (saveStatus) saveStatus.textContent = geometryRequired ? 'Changes saved. Review and approve your design.' : 'Changes saved. Production review required.';
        return true;
      } catch(error) {
        if (saveStatus) saveStatus.textContent = 'Could not save edited design: '+String(error.message||error);
        return false;
      } finally {busySaving=false;sync();}
    }
    root.sdEnsureProductionCapture = async () => {
      if (root.dataset.sdGeometryCaptureEnabled !== 'true'||busyBackground||backgroundFailed) return false;
      if (root.dataset.sdGeometryCapturedToken === root.dataset.sdArtworkToken && !isUnsaved()) return true;
      return saveEditedVersion(true);
    };
    if (saveButton) saveButton.addEventListener('click', () => { void saveEditedVersion(false); });
    // Extra guard for pilot: every altered visual MUST be persisted as a separate file first.
    // Existing guarded client enforces root.dataset.sdStudioNeedsSave before enabling cart.
    window.addEventListener('pagehide', () => { stopBackgroundRequest();if (objectUrl) URL.revokeObjectURL(objectUrl); }, { once:true });
    sync();schedule();
    if (input?.files?.length) setFile(input.files[0]);
  }
  function boot() { document.querySelectorAll('[data-sd-configurator]').forEach(setup); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once:true }); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
